import { api } from "./client";

export interface AccessRequestItem {
  id: string;
  request_id: string;
  email: string;
  full_name: string;
  company: string;
  role: string;
  reason: string;
  website?: string;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  updated_at: string;
  reviewed_by?: string;
  reviewer_email?: string;
  reviewed_at?: string;
  rejection_reason?: string;
  invitation_status: "not_sent" | "sent" | "accepted" | "failed";
  invitation_sent_at?: string;
  invitation_expires_at?: string;
  invitation_accepted_at?: string;
  assigned_role?: string;
  assigned_plan?: string;
}

export interface AccessRequestListResponse {
  items: AccessRequestItem[];
  total: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  skip: number;
  limit: number;
}

export interface SubmitAccessRequestPayload {
  full_name: string;
  email: string;
  company: string;
  role: string;
  reason: string;
  website?: string;
  turnstile_token?: string;
}

export interface SubmitAccessRequestResponse {
  status: string;
  message: string;
}

export interface VerifyInvitationResponse {
  valid: boolean;
  email: string;
  full_name: string;
  company?: string;
  role?: string;
  expires_at?: string;
}

export interface AcceptInvitationPayload {
  token: string;
  password: string;
  confirm_password?: string;
  turnstile_token?: string;
}

export interface AcceptInvitationResponse {
  status: string;
  message: string;
}

/**
 * Public: Submits an access request during pre-launch mode.
 */
export async function submitAccessRequest(
  payload: SubmitAccessRequestPayload
): Promise<SubmitAccessRequestResponse> {
  const res = await api.post<SubmitAccessRequestResponse>("/access-requests", payload);
  return res.data;
}

/**
 * Public: Verifies single-use invitation token before showing password setup form.
 */
export async function verifyInvitationToken(token: string): Promise<VerifyInvitationResponse> {
  const res = await api.get<VerifyInvitationResponse>(
    `/access-requests/verify-invitation?token=${encodeURIComponent(token)}`
  );
  return res.data;
}

/**
 * Public: Accepts invitation, configures user credentials, and activates account.
 */
export async function acceptInvitation(
  payload: AcceptInvitationPayload
): Promise<AcceptInvitationResponse> {
  const res = await api.post<AcceptInvitationResponse>("/access-requests/accept-invitation", payload);
  return res.data;
}

/**
 * Admin: Fetches paginated access requests with status filtering and search.
 */
export async function fetchAdminAccessRequests(params?: {
  search?: string;
  status?: string;
  skip?: number;
  limit?: number;
  sort?: string;
  direction?: string;
}): Promise<AccessRequestListResponse> {
  const res = await api.get<AccessRequestListResponse>("/admin/access-requests", {
    params: {
      search: params?.search || undefined,
      status: params?.status && params.status !== "all" ? params.status : undefined,
      skip: params?.skip ?? 0,
      limit: params?.limit ?? 20,
      sort: params?.sort ?? "created_at",
      direction: params?.direction ?? "desc",
    },
  });
  return res.data;
}

/**
 * Admin: Fetches single access request detail.
 */
export async function fetchAdminAccessRequest(id: string): Promise<AccessRequestItem> {
  const res = await api.get<AccessRequestItem>(`/admin/access-requests/${id}`);
  return res.data;
}

/**
 * Admin: Atomically approves an access request and triggers invitation email.
 */
export async function approveAdminAccessRequest(
  id: string,
  payload?: { assigned_role?: string; assigned_plan?: string }
): Promise<AccessRequestItem> {
  const res = await api.post<AccessRequestItem>(`/admin/access-requests/${id}/approve`, payload || {});
  return res.data;
}

/**
 * Admin: Atomically rejects an access request with optional explanation.
 */
export async function rejectAdminAccessRequest(
  id: string,
  rejection_reason?: string
): Promise<AccessRequestItem> {
  const res = await api.post<AccessRequestItem>(`/admin/access-requests/${id}/reject`, {
    rejection_reason: rejection_reason || undefined,
  });
  return res.data;
}

/**
 * Admin: Regenerates and resends a fresh invitation token.
 */
export async function resendAdminInvitation(id: string): Promise<AccessRequestItem> {
  const res = await api.post<AccessRequestItem>(`/admin/access-requests/${id}/resend-invitation`, {});
  return res.data;
}
